import React, { useState } from 'react';
import { Button, Form, Modal as AntModal, Space, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined, SaveOutlined, DragOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { PrimaryButton } from 'utils';
import { adminServices } from 'services';
import { AlertActions } from 'redux/actions';
import PromotionCategoryModal from './PromotionCategoryModal';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const TableWrapper = styled.div`
  .ant-table {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ActionButton = styled(Button)`
  border: none;
  background: transparent;
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
`;

const DragHandle = styled.span`
  cursor: grab;
  margin-right: 8px;
`;

const StyledTable = styled.div`
  width: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  .table-header, .table-row {
    display: flex;
    align-items: center;
    padding: 0 16px;
    min-height: 48px;
    border-bottom: 1px solid #f0f0f0;
  }
  .table-header {
    background: #f7f7f7;
    font-weight: 600;
    color: #333;
    font-size: 15px;
  }
  .table-row:last-child {
    border-bottom: none;
  }
  .cell {
    padding: 8px 0;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cell.id { width: 60px; flex: none; }
  .cell.actions { width: 180px; flex: none; display: flex; gap: 4px; justify-content: flex-end; }
`;

const PromotionCategoriesTable = ({ categories, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();
  const [orderList, setOrderList] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);
  React.useEffect(() => {
    setOrderList(
      [...(categories || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    );
    setOrderChanged(false);
  }, [categories]);

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    setShowModal(true);
    document.body.style.overflowY = 'hidden';
  };

  const handleEdit = (record) => {
    setEditingCategory(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description
    });
    setShowModal(true);
    document.body.style.overflowY = 'hidden';
  };

  const handleDelete = async (id) => {
    AntModal.confirm({
      title: 'Kategorie löschen',
      content: 'Sind Sie sicher, dass Sie diese Kategorie löschen möchten?',
      onOk: async () => {
        try {
          setLoading(true);
          await adminServices.deletePromotionCategory(id);
          AlertActions.success('Kategorie erfolgreich gelöscht!', 'Erfolg');
          onRefresh();
        } catch (error) {
          AlertActions.error(error.toString(), 'Fehler');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (editingCategory) {
        await adminServices.updatePromotionCategory(editingCategory.id, values);
        AlertActions.success('Kategorie erfolgreich aktualisiert!', 'Erfolg');
      } else {
        await adminServices.addPromotionCategory(values);
        AlertActions.success('Kategorie erfolgreich hinzugefügt!', 'Erfolg');
      }
      setShowModal(false);
      document.body.style.overflowY = 'unset';
      onRefresh();
    } catch (error) {
      AlertActions.error(error.toString(), 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    document.body.style.overflowY = 'unset';
  };

  // Drag & Drop
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newList = Array.from(orderList);
    const [removed] = newList.splice(result.source.index, 1);
    newList.splice(result.destination.index, 0, removed);
    setOrderList(newList);
    setOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    setLoading(true);
    try {
      const orders = orderList.map((cat, idx) => ({ id: cat.id, order: idx + 1 }));
      await adminServices.updatePromotionCategoriesOrder({ orders });
      AlertActions.success('Reihenfolge erfolgreich gespeichert!', 'Erfolg');
      setOrderChanged(false);
      onRefresh();
    } catch (e) {
      console.log(e);
      AlertActions.error('Fehler beim Reihenfolge speichern', 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const moveRow = async (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === orderList.length - 1)
    )
      return;
    const newList = Array.from(orderList);
    const [removed] = newList.splice(index, 1);
    newList.splice(index + direction, 0, removed);
    setOrderList(newList);
    setOrderChanged(true);
    try {
      await adminServices.updatePromotionCategoryOrder(removed.id, { order: index + direction + 1 });
      onRefresh();
    } catch (e) {
      // ignore
    }
  };

  return (
    <>
      <HeaderWrapper>
        <Title>Promotion Kategorien</Title>
        <Space>
          <PrimaryButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Neue Kategorie
          </PrimaryButton>
          <PrimaryButton
            type="default"
            icon={<SaveOutlined />}
            onClick={handleSaveOrder}
            disabled={!orderChanged}
            loading={loading}
          >
            Reihenfolge speichern
          </PrimaryButton>
        </Space>
      </HeaderWrapper>
      <StyledTable>
        <div className="table-header">
          <div className="cell id"></div>
          <div className="cell id">#</div>
          <div className="cell">Name</div>
          <div className="cell">Beschreibung</div>
          <div className="cell actions">Aktionen</div>
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="categories-droppable">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {orderList.map((cat, idx) => (
                  <Draggable key={cat.id} draggableId={cat.id.toString()} index={idx}>
                    {(provided, snapshot) => (
                      <div
                        className="table-row"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          background: snapshot.isDragging ? '#f0f0f0' : '#fff',
                          ...provided.draggableProps.style,
                        }}
                      >
                        <div className="cell id">
                          <DragHandle {...provided.dragHandleProps}>
                            <DragOutlined />
                          </DragHandle>
                        </div>
                        <div className="cell id">{cat.order}</div>
                        <div className="cell">{cat.name}</div>
                        <div className="cell">{cat.description || '-'}</div>
                        <div className="cell actions">
                          <ActionButton
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(cat)}
                            title="Bearbeiten"
                          />
                          <ActionButton
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(cat.id)}
                            title="Löschen"
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </StyledTable>

      <PromotionCategoryModal
        showModal={showModal}
        editingCategory={editingCategory}
        loading={loading}
        form={form}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default PromotionCategoriesTable;
